import React, { useEffect, useRef, useState } from "react";
import "./ClinicalAudioEqualizer.css";

const EQ_BANDS = Object.freeze([
  { id: "40", frequency: 40, label: "40 Hz" },
  { id: "80", frequency: 80, label: "80 Hz" },
  { id: "160", frequency: 160, label: "160 Hz" },
  { id: "320", frequency: 320, label: "320 Hz" },
  { id: "640", frequency: 640, label: "640 Hz" },
  { id: "1280", frequency: 1280, label: "1.28 kHz" },
]);

const DEFAULT_BOOST_DB = 6;
const FLAT_GAINS = Object.freeze({
  40: 0,
  80: 0,
  160: 0,
  320: 0,
  640: 0,
  1280: 0,
});
const HEART_FOCUS_GAINS = Object.freeze({
  40: 6,
  80: 5,
  160: 3,
  320: 1,
  640: 0,
  1280: 0,
});

const dbToGain = (decibels) => 10 ** (decibels / 20);
const formatDb = (value) => `${value > 0 ? "+" : ""}${value} dB`;

const setAudioParam = (audioParam, value, audioContext, immediate = false) => {
  const now = audioContext.currentTime;
  audioParam.cancelScheduledValues(now);
  if (immediate) {
    audioParam.setValueAtTime(value, now);
  } else {
    audioParam.setTargetAtTime(value, now, 0.015);
  }
};

const applySettings = (graph, settings, immediate = false) => {
  if (!graph || graph.context.state === "closed") return;
  const { context, preamp, filters } = graph;
  const boostDb = settings.bypassed ? 0 : settings.boostDb;
  setAudioParam(preamp.gain, dbToGain(boostDb), context, immediate);
  filters.forEach(({ id, node }) => {
    const bandGain = settings.bypassed ? 0 : settings.bandGains[id];
    setAudioParam(node.gain, bandGain, context, immediate);
  });
};

const disconnectNode = (node) => {
  try {
    node?.disconnect?.();
  } catch {
    // The node may already be disconnected during rapid stream replacement.
  }
};

const getEngineLabel = (engineState, streamState, hasAudio) => {
  if (!hasAudio) {
    if (streamState === "reconnecting") return "Reconnecting stethoscope audio";
    if (streamState === "streaming") return "Waiting for audio media";
    return "Waiting for stethoscope audio";
  }
  if (engineState === "running") return "Audio playing";
  if (engineState === "suspended" || engineState === "interrupted") {
    return "Audio paused on this workstation";
  }
  if (engineState === "unsupported") return "Web Audio is unavailable";
  if (engineState === "failed") return "Audio processor unavailable";
  if (engineState === "closed") return "Audio processor closed";
  return "Preparing audio processor";
};

function ClinicalAudioEqualizer({ stream, streamState }) {
  const graphRef = useRef(null);
  const settingsRef = useRef(null);
  const [boostDb, setBoostDb] = useState(DEFAULT_BOOST_DB);
  const [bandGains, setBandGains] = useState(() => ({ ...FLAT_GAINS }));
  const [bypassed, setBypassed] = useState(false);
  const [engineState, setEngineState] = useState("waiting");
  const [engineError, setEngineError] = useState("");
  const [isPlaybackBusy, setIsPlaybackBusy] = useState(false);

  settingsRef.current = { boostDb, bandGains, bypassed };
  const hasAudio = Boolean(
    stream?.getAudioTracks?.().some((track) => track.readyState !== "ended"),
  );

  useEffect(() => {
    setIsPlaybackBusy(false);
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      setEngineState("unsupported");
      setEngineError("This browser cannot provide live audio equalization.");
      return undefined;
    }

    let context;
    try {
      context = new AudioContextClass({ latencyHint: "interactive" });
      const preamp = context.createGain();
      const filters = EQ_BANDS.map((band) => {
        const node = context.createBiquadFilter();
        node.type = "peaking";
        node.frequency.setValueAtTime(band.frequency, context.currentTime);
        node.Q.setValueAtTime(1.1, context.currentTime);
        return { id: band.id, node };
      });
      const limiter = context.createDynamicsCompressor();
      limiter.threshold.setValueAtTime(-10, context.currentTime);
      limiter.knee.setValueAtTime(2, context.currentTime);
      limiter.ratio.setValueAtTime(20, context.currentTime);
      limiter.attack.setValueAtTime(0.003, context.currentTime);
      limiter.release.setValueAtTime(0.2, context.currentTime);
      const outputGain = context.createGain();
      outputGain.gain.setValueAtTime(0.85, context.currentTime);
      let previousNode = preamp;
      filters.forEach(({ node }) => {
        previousNode.connect(node);
        previousNode = node;
      });
      previousNode.connect(limiter);
      limiter.connect(outputGain);
      outputGain.connect(context.destination);

      const graph = {
        context,
        source: null,
        preamp,
        filters,
        limiter,
        outputGain,
      };
      graphRef.current = graph;
      applySettings(graph, settingsRef.current, true);
      setEngineError("");
      setEngineState(context.state);

      context.onstatechange = () => {
        if (graphRef.current === graph) setEngineState(context.state);
      };

      Promise.resolve(context.resume())
        .catch((error) => {
          if (graphRef.current === graph && error?.name !== "NotAllowedError") {
            setEngineError(error?.message || "Unable to start audio playback.");
          }
        })
        .finally(() => {
          if (graphRef.current === graph) setEngineState(context.state);
        });

      return () => {
        if (graphRef.current === graph) graphRef.current = null;
        context.onstatechange = null;
        const activeSource = graph.source;
        graph.source = null;
        disconnectNode(activeSource);
        disconnectNode(preamp);
        filters.forEach(({ node }) => disconnectNode(node));
        disconnectNode(limiter);
        disconnectNode(outputGain);
        if (context.state !== "closed") {
          void context.close().catch(() => undefined);
        }
      };
    } catch (error) {
      if (context && context.state !== "closed") void context.close().catch(() => undefined);
      setEngineState("failed");
      setEngineError(error?.message || "Unable to initialize live audio processing.");
      return undefined;
    }
  }, []);

  useEffect(() => {
    const graph = graphRef.current;
    const audioTrack = stream?.getAudioTracks?.()[0];
    if (!graph) return undefined;
    if (!audioTrack || audioTrack.readyState === "ended") {
      setEngineState(graph.context.state);
      setEngineError("");
      return undefined;
    }

    let source;
    try {
      disconnectNode(graph.source);
      graph.source = null;
      source = graph.context.createMediaStreamSource(stream);
      graph.source = source;
      source.connect(graph.preamp);
      setEngineState(graph.context.state);
      setEngineError("");
    } catch (error) {
      if (graph.source === source) graph.source = null;
      disconnectNode(source);
      setEngineState("failed");
      setEngineError(error?.message || "Unable to attach the live stethoscope audio.");
      return undefined;
    }

    return () => {
      if (graph.source === source) graph.source = null;
      disconnectNode(source);
    };
  }, [stream]);

  useEffect(() => {
    applySettings(graphRef.current, settingsRef.current);
  }, [bandGains, boostDb, bypassed]);

  const togglePlayback = async () => {
    const graph = graphRef.current;
    if (!graph || isPlaybackBusy) return;
    setIsPlaybackBusy(true);
    try {
      if (graph.context.state === "running") await graph.context.suspend();
      else await graph.context.resume();
      if (graphRef.current === graph) {
        setEngineState(graph.context.state);
        setEngineError("");
      }
    } catch (error) {
      if (graphRef.current === graph) {
        setEngineError(error?.message || "Unable to change audio playback.");
      }
    } finally {
      if (graphRef.current === graph) setIsPlaybackBusy(false);
    }
  };

  const updateBand = (bandId, value) => {
    setBandGains((currentGains) => ({
      ...currentGains,
      [bandId]: Number(value),
    }));
  };

  const applyHeartFocus = () => {
    setBoostDb(23);
    setBandGains({ ...HEART_FOCUS_GAINS });
    setBypassed(false);
  };

  const resetEqualizer = () => {
    setBoostDb(DEFAULT_BOOST_DB);
    setBandGains({ ...FLAT_GAINS });
    setBypassed(false);
  };

  const engineLabel = getEngineLabel(engineState, streamState, hasAudio);
  const canControlPlayback = hasAudio
    && !["unsupported", "failed", "waiting", "closed"].includes(engineState);

  return (
    <div className="clinical-audio-eq" aria-label="Stethoscope listening controls">
      <div className="clinical-audio-eq__toolbar">
        <div className="clinical-audio-eq__engine-status" role="status" aria-live="polite">
          <span
            className={`clinical-audio-eq__status-light${hasAudio && engineState === "running" ? " clinical-audio-eq__status-light--live" : ""}`}
            aria-hidden="true"
          />
          <div>
            <strong>{engineLabel}</strong>
            <span>Processed locally on this workstation</span>
          </div>
        </div>
        <div className="clinical-audio-eq__actions">
          <button
            type="button"
            onClick={togglePlayback}
            disabled={!canControlPlayback || isPlaybackBusy}
          >
            {engineState === "running" ? "Mute locally" : "Start listening"}
          </button>
          <button
            type="button"
            className={bypassed ? "clinical-audio-eq__button--active" : ""}
            onClick={() => setBypassed((currentValue) => !currentValue)}
            disabled={!hasAudio}
            aria-pressed={bypassed}
          >
            Bypass tuning
          </button>
          <button type="button" onClick={resetEqualizer}>Reset</button>
        </div>
      </div>

      {engineError && <div className="clinical-audio-eq__notice" role="alert">{engineError}</div>}

      <div className={`clinical-audio-eq__tuning${bypassed ? " clinical-audio-eq__tuning--bypassed" : ""}`}>
        <section className="clinical-audio-eq__boost-panel" aria-label="Input boost">
          <div className="clinical-audio-eq__section-heading">
            <div>
              <strong>Input boost</strong>
              <span>Protected by output limiting</span>
            </div>
            <output>{formatDb(boostDb)}</output>
          </div>
          <input
            type="range"
            min="0"
            max="24"
            step="1"
            value={boostDb}
            onChange={(event) => setBoostDb(Number(event.target.value))}
            disabled={!hasAudio || bypassed}
            aria-label="Stethoscope input boost"
          />
          <div className="clinical-audio-eq__boost-scale" aria-hidden="true">
            <span>0 dB</span>
            <span>+24 dB</span>
          </div>
          <button type="button" onClick={applyHeartFocus} disabled={!hasAudio}>
            Heart focus
          </button>
          <p>Headphones are recommended for clearer low-level sounds.</p>
        </section>

        <section className="clinical-audio-eq__bands-panel" aria-label="Six band equalizer">
          <div className="clinical-audio-eq__section-heading">
            <div>
              <strong>Frequency equalizer</strong>
              <span>Adjust each band from -12 to +12 dB</span>
            </div>
            <span>6 band</span>
          </div>
          <div className="clinical-audio-eq__bands">
            {EQ_BANDS.map((band) => (
              <label key={band.id} className="clinical-audio-eq__band">
                <output>{formatDb(bandGains[band.id])}</output>
                <span className="clinical-audio-eq__slider-wrap">
                  <input
                    type="range"
                    min="-12"
                    max="12"
                    step="1"
                    value={bandGains[band.id]}
                    onChange={(event) => updateBand(band.id, event.target.value)}
                    disabled={!hasAudio || bypassed}
                    aria-label={`${band.label} equalizer gain`}
                  />
                </span>
                <span>{band.label}</span>
              </label>
            ))}
          </div>
        </section>
      </div>
      <p className="clinical-audio-eq__safety-note">
        Listening aid: use Bypass tuning to compare with the untuned signal.
      </p>
    </div>
  );
}

export default ClinicalAudioEqualizer;
